import React, { useState } from 'react'
import { makeStyles, Typography, Container, TableContainer, Paper, Button, CircularProgress } from '@material-ui/core'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import { useNotify, useTranslate } from 'react-admin'
import { useQuery } from 'react-query'
import teamStatisticsPageStyles from './teamStatisticsPage.styles'
import COLORS from '../../utils/constants/colors'
import useQueryParams from '../../hooks/useQueryParams'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import ContainerPadding from '../../components/containerPadding/containerPadding'
import QueryKeys from '../../utils/constants/queryKeys'
import getGameStats from '../../Apis/stats/getGameStats'
import GameStatsInfo from './GameStatsInfo'
import PerQuarterTable from './PerQuarterTable'
import PlayersPerGameTable from './PlayerPerGameTable'
import MyToggleButton from '../../components/MyToggleButton/MyToggleButton'
import NoStatsAvailablePage from './NoStatsAvalaiblePage'
import { useCmsScope } from '../../context/useCmsScope'
import getGameStatsSheet from '../../Apis/stats/getGameStatsSheet'
import downloadStatisticsPdf from '../../utils/helpers/gameStatsSheet/downloadStatisticsPdf'

const GameStatisticsPage = () => {
  const useStyles = makeStyles(teamStatisticsPageStyles)
  const classes = useStyles()
  const [toggleValue, setToggleValue] = useState('team1Players')
  const translate = useTranslate()
  const notify = useNotify()
  const queryParams = useQueryParams()
  // The org whose CMS is shown: the federation for a paired club, otherwise the selected org.
  // A paired club reading a federation game gets the PDF too: the sheet endpoint accepts the
  // same read permission as the boxscore it already shows.
  const { orgId } = useCmsScope()

  const {
    data: gameStats,
    isLoading: isGameStatsLoading,
    error: gameStatsError,
  } = useQuery(
    [QueryKeys.GET_GAME_STATS, queryParams?.eventId, orgId],
    () => getGameStats({ gameId: queryParams?.eventId, orgId }),
    {
      enabled: Boolean(queryParams?.eventId && orgId),
      keepPreviousData: true,
    },
  )

  // The FIBA statistics sheet. Every number comes from the backend's /sheet response (boxscore,
  // header, play-by-play derived numbers); the browser only lays them out on the template.
  // Only offered for a game with recorded play-by-play: not before it starts, not for a forfeit,
  // and not for a score entered by hand (nobody played), which would print an empty sheet.
  const anyonePlayed = Boolean(
    gameStats?.teams?.some((team) => team.teamPlayerStats?.some((p) => p.played)),
  )
  const canDownloadPdf =
    anyonePlayed && gameStats?.status !== 'Upcoming' && gameStats?.status !== 'Forfeit'

  const [isDownloading, setIsDownloading] = useState(false)
  const onDownloadPdf = async () => {
    setIsDownloading(true)
    try {
      const sheet = await getGameStatsSheet({ gameId: queryParams?.eventId, orgId })
      await downloadStatisticsPdf(sheet)
    } catch (err) {
      notify(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not create the statistics PDF',
        'warning',
      )
    } finally {
      setIsDownloading(false)
    }
  }

  const selectedTeam = toggleValue === 'team1Players' ? gameStats?.teams[0] : gameStats?.teams[1]
  const players = selectedTeam?.teamPlayerStats
  const teamOnlyStats = selectedTeam?.teamOnlyStats
  const totalStats = selectedTeam?.totalStats

  return (
    <Container>
      {isGameStatsLoading ? (
        <ContainerPadding paddingValue='45%'>
          <LoaderDots
            style={{
              fill: COLORS.orange,
              width: 70,
              height: 70,
            }}
          />
        </ContainerPadding>
      ) : (
        <ContainerPadding paddingValue='1%' className={classes.statsContainer}>
          {!gameStats || gameStatsError ? (
            <NoStatsAvailablePage error={gameStatsError?.message} />
          ) : (
            <ContainerPadding paddingValue='1%'>
              {canDownloadPdf && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={
                      isDownloading ? <CircularProgress size={14} /> : <PictureAsPdfIcon />
                    }
                    disabled={isDownloading}
                    onClick={onDownloadPdf}
                  >
                    PDF
                  </Button>
                </div>
              )}
              <GameStatsInfo gameStats={gameStats} eventId={queryParams?.eventId} />
              <Typography variant='h6' gutterBottom style={{ marginTop: '50px' }}>
                {translate('ra.text.perQuarter')}
              </Typography>
              <PerQuarterTable teams={gameStats.teams} gameTimeInfo={gameStats.gameTime} />

              <Typography variant='h6' gutterBottom style={{ marginTop: '50px' }}>
                {translate('ra.text.endOfQuarter')}
              </Typography>
              <PerQuarterTable
                teams={gameStats.teams}
                gameTimeInfo={gameStats.gameTime}
                cummulative={true}
              />

              <Typography
                variant='h6'
                gutterBottom
                style={{ marginTop: '50px', display: 'flex', justifyContent: 'space-between' }}
              >
                <span>{translate('ra.text.selectedPlayers')}</span>
                <MyToggleButton
                  left={{
                    value: 'team1Players',
                    label: gameStats?.teams[0].name,
                  }}
                  right={{
                    value: 'team2Players',
                    label: gameStats?.teams[1]?.name,
                  }}
                  setToggleValue={setToggleValue}
                  toggleValue={toggleValue}
                />
              </Typography>
              <TableContainer component={Paper}>
                <PlayersPerGameTable
                  players={players}
                  teamOnlyStats={teamOnlyStats}
                  totalStats={totalStats}
                  eventTeamId={selectedTeam?.eventTeamId}
                  seasonTeamId={selectedTeam?.seasonTeamId}
                  gameId={queryParams?.eventId}
                  displayStats={gameStats.displayStats}
                />
              </TableContainer>
            </ContainerPadding>
          )}
        </ContainerPadding>
      )}
    </Container>
  )
}

export default GameStatisticsPage
